import React, { useCallback, useEffect, useState, useRef } from 'react';
import { VenueWithMenus } from 'components/SingleItem/Details';
import SidePanel from 'components/SidePanel/SidePanel';
import { AmplitudeEventCategories, logAmplitudeEvent } from 'utils/Amplitude';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useOperatingHours } from 'utils/useOperatingHours';
import DetailsFooter from 'components/SingleItem/DetailsFooter';
import type { OperatingHours } from 'generated/graphql';
import { type ClosedDates, type DayOfWeekType, SnackBarTypeEnum } from 'types';
import { useModalContext } from 'common/Context/ModalContext';
import Switch from 'components/Inputs/Switch';
import { DAYS_OF_WEEK } from 'consts';
import { cloneDeep } from 'lodash';
import { useAppContext } from 'AppContext';
import ClosedHours from './ClosedHours';
import AllMenuHours from './AllMenuHours';
import styles from './MenuHoursAndDates.module.scss';
import DayOfWeek from './DayOfWeek';

const ALL_MENU_HOURS_TAB_KEY = 'allMenuHours';
const CLOSED_HOURS_TAB_KEY = 'specialHours';

const MENU_AVAILABILITY_TABS = [
  {
    label: 'Menu hours',
    value: ALL_MENU_HOURS_TAB_KEY
  },
  {
    label: 'Closed dates',
    value: CLOSED_HOURS_TAB_KEY
  }
];

interface Props {
  currentVenue?: VenueWithMenus;
}

const MenuHoursAndDates: React.FC<Props> = ({ currentVenue }) => {
  const [activeTab, setActiveTab] = useState<string>('allMenuHours');
  const [hasSameHoursForAllMenus, setHasSameHoursForAllMenus] = useState<boolean>(false);
  const [appsWithoutHours, setAppsWithoutHours] = useState<string[]>([]);
  const { openModal, openSnackBar } = useModalContext();

  const [searchParams] = useSearchParams();

  const {
    currentActiveClosures,
    submissionStatus,
    venueMMDates,
    setVenueMMDates,
    setCurrentActiveClosures,
    hasUnsavedChanges,
    clearAllUnsavedChanges,
    commonHoursAcrossAllMenus,
    setCommonHoursAcrossAllMenus,
    subscribedRecurringHoursSupportedApps,
    submitHours,
    hasCommonHoursAcrossAllMenusBeenApplied
  } = useOperatingHours({
    currentVenue
  });

  const menuRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { allMMHours } = useAppContext();

  const location = useLocation();
  const pathnameWithoutHours = location.pathname.split(`/hours`)[0];
  const navigate = useNavigate();

  const displaySnackBar = useCallback(
    (message: string, type: SnackBarTypeEnum): void => {
      openSnackBar(message, undefined, undefined, type, 3000);
    },
    [openSnackBar]
  );

  useEffect(() => {
    // Do not apply common hours when navigated from SingleMenuHours
    if (commonHoursAcrossAllMenus && searchParams.get('fromSingleMenuHours') !== 'true') {
      setHasSameHoursForAllMenus(true);
    }
  }, [commonHoursAcrossAllMenus, searchParams]);

  // Based on updates to submissionStatus, log events, show feedback messages, reset submission state and navigate.
  useEffect(() => {
    if (submissionStatus === 'SUCCESS') {
      logAmplitudeEvent({
        name: 'click update (success)',
        category: AmplitudeEventCategories.MM_SIDE_PANEL_ALL_ITEMS_OPERATING_HOURS
      });
      displaySnackBar('Operating hours have been updated.', SnackBarTypeEnum.SUCCESS);
      navigate(pathnameWithoutHours);
    } else if (submissionStatus === 'FAILURE') {
      logAmplitudeEvent({
        name: 'click update (failure)',
        category: AmplitudeEventCategories.MM_SIDE_PANEL_ALL_ITEMS_OPERATING_HOURS
      });
      displaySnackBar(
        'An error occurred while updating operating hours. Please try again later.',
        SnackBarTypeEnum.INFO
      );
      navigate(pathnameWithoutHours);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionStatus]);

  // When common hour is applied check for apps without hours
  useEffect(() => {
    if (allMMHours && hasCommonHoursAcrossAllMenusBeenApplied && subscribedRecurringHoursSupportedApps.length > 0) {
      const missingHoursApps = subscribedRecurringHoursSupportedApps.reduce<string[]>((acc, app) => {
        if (!allMMHours[app.id] || !Object.keys(allMMHours[app.id]).length) {
          acc.push(app.name);
        }
        return acc;
      }, []);
      setAppsWithoutHours(missingHoursApps);
    } else {
      setAppsWithoutHours([]);
    }
  }, [hasCommonHoursAcrossAllMenusBeenApplied, subscribedRecurringHoursSupportedApps, allMMHours]);

  const closeModal = (): void => {
    logAmplitudeEvent({
      name: 'click cancel',
      category: AmplitudeEventCategories.MM_SIDE_PANEL_MENU_OPERATING_HOURS
    });
    setTimeout(() => navigate(pathnameWithoutHours), 100);
  };

  const handleCancel = (): void => {
    if (hasUnsavedChanges) {
      openModal({
        type: 'CANCEL_CONFIRM',
        callback: closeModal
      });
    } else {
      closeModal();
    }
  };

  const handleTabChange = (value: string): void => {
    if (activeTab === 'specialHours' && hasUnsavedChanges) {
      openModal({
        type: 'CANCEL_CONFIRM',
        callback: () => {
          clearAllUnsavedChanges();
          setActiveTab(value);
        }
      });
    } else {
      setActiveTab(value);
    }
  };

  const handleUpdate = useCallback(async (): Promise<void> => {
    const dayHoursWithError = Object.values(commonHoursAcrossAllMenus || {}).find((dayHours) => dayHours.error);
    // Scroll to the error
    if (dayHoursWithError) {
      const errorRefIndex = DAYS_OF_WEEK.indexOf(dayHoursWithError.day);
      menuRefs.current[errorRefIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const hasValidationError = venueMMDates.some((date) => date.error);
    if (hasValidationError) {
      return;
    }
    logAmplitudeEvent({
      name: 'click update',
      category: AmplitudeEventCategories.MM_SIDE_PANEL_ALL_ITEMS_OPERATING_HOURS
    });
    submitHours();
  }, [commonHoursAcrossAllMenus, venueMMDates, submitHours]);

  const handleUpdateSavedActiveClosures = (updatedClosures: OperatingHours[]): void => {
    setCurrentActiveClosures(updatedClosures);
  };

  const handleUpdateVenueMMDates = (updatedClosures: ClosedDates[]): void => {
    setVenueMMDates(updatedClosures);
  };

  const handleSameHoursForAllMenusToggle = (isChecked: boolean): void => {
    logAmplitudeEvent({
      name: isChecked ? 'toggle apply to all apps on' : 'toggle apply to all apps off',
      category: AmplitudeEventCategories.MM_SIDE_PANEL_ALL_ITEMS_OPERATING_HOURS
    });
    setHasSameHoursForAllMenus(isChecked);
  };

  const handleDayTimeChange = (dayHours: DayOfWeekType): void => {
    if (!commonHoursAcrossAllMenus) return;
    const { day, hours } = dayHours;
    const updatedMenuHours = cloneDeep(commonHoursAcrossAllMenus);
    updatedMenuHours[day] = { day, hours };
    setCommonHoursAcrossAllMenus(updatedMenuHours);
  };

  return (
    <div className={styles.MenuHoursAndDates}>
      <SidePanel
        title="Menu hours & dates"
        description="Manage the days and times that your app menus are available to your customers."
        route="hours"
        closeModal={handleCancel}
      >
        <ul className={styles.Tabs}>
          {MENU_AVAILABILITY_TABS.map(({ label, value }) => {
            return (
              <li key={label} className={activeTab === value ? styles.Active : ''}>
                <button
                  onClick={() => handleTabChange(value)}
                  id={`details-${value}-button`}
                  data-testid={`operating-hours-${value}-tab-button`}
                  type="button"
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
        <div className={styles.TabContent}>
          {activeTab === ALL_MENU_HOURS_TAB_KEY && (
            <>
              <div className={styles.Header}>
                {hasCommonHoursAcrossAllMenusBeenApplied && appsWithoutHours.length > 0 && (
                  <div className={styles.Alert} data-testid="apps-without-hours-alert">
                    <i className="mm-error-circle" />
                    {appsWithoutHours.join(', ')} hours need to be reviewed and confirmed by clicking ‘Update all apps’.
                  </div>
                )}
                <div> Specify the different times customers will be able to order online from the apps.</div>
                {subscribedRecurringHoursSupportedApps.length > 1 && commonHoursAcrossAllMenus && (
                  <div className={styles.ApplyToAllApps}>
                    <div className={styles.Toggle}>
                      <div>Apply the same hours to all supported app menus</div>
                      <Switch
                        aria-labelledby="apply-to-all-apps-toggle-switch"
                        data-testid="apply-to-all-apps-toggle-switch"
                        isChecked={hasSameHoursForAllMenus}
                        onUpdate={handleSameHoursForAllMenusToggle}
                      />
                    </div>
                    <div className={styles.SupportedApps}>
                      {subscribedRecurringHoursSupportedApps.map((app) => (
                        <img key={app.id} src={app.image || '/Images/NoApp.png'} alt={`Icon for ${app.name}`} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {hasSameHoursForAllMenus ? (
                <div className={styles.CommonHours}>
                  {DAYS_OF_WEEK.map(
                    (day, index) =>
                      commonHoursAcrossAllMenus?.[day] && ( // Ensure defaultMenuHours[day] exists before rendering
                        <DayOfWeek
                          key={day}
                          dayHours={commonHoursAcrossAllMenus[day]}
                          onChange={handleDayTimeChange}
                          ref={(el) => (menuRefs.current[index] = el)}
                        />
                      )
                  )}
                </div>
              ) : (
                <AllMenuHours currentVenue={currentVenue} />
              )}
              {hasSameHoursForAllMenus && (
                <div className={styles.Footer}>
                  <DetailsFooter
                    closeModal={handleCancel}
                    handleConfirm={handleUpdate}
                    confirmButton={
                      subscribedRecurringHoursSupportedApps.length > 1
                        ? 'Update all apps'
                        : `Update ${subscribedRecurringHoursSupportedApps[0].name}`
                    }
                    loading={submissionStatus === 'PENDING'}
                  />
                </div>
              )}
            </>
          )}
          {activeTab === CLOSED_HOURS_TAB_KEY && (
            <>
              <ClosedHours
                currentVenue={currentVenue}
                currentActiveClosures={currentActiveClosures}
                venueMMDates={venueMMDates}
                onChangeSavedActiveClosures={handleUpdateSavedActiveClosures}
                onChangeVenueMMDates={handleUpdateVenueMMDates}
              />

              <div className={styles.Footer}>
                <DetailsFooter
                  closeModal={handleCancel}
                  handleConfirm={handleUpdate}
                  confirmButton="Update"
                  loading={submissionStatus === 'PENDING'}
                />
              </div>
            </>
          )}
        </div>
      </SidePanel>
    </div>
  );
};

export default MenuHoursAndDates;
